import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Mail, MessageSquare, ArrowUpRight, ArrowDownLeft, ArrowLeft,
  Clock, Download, Filter, Search, Smartphone, Calendar,
  AlertCircle, CheckCircle, Send, Upload, Edit3, GitBranch, FileDown, X
} from 'lucide-react';
import client from '../api/client';

const ACTIVITY_CONFIG = {
  doc_generated: { icon: FileText, color: 'bg-blue-100 text-blue-600', label: 'Document Generated' },
  email_sent: { icon: Mail, color: 'bg-green-100 text-green-600', label: 'Email Sent' },
  sms_sent: { icon: Smartphone, color: 'bg-emerald-100 text-emerald-600', label: 'SMS Sent' },
  sms_received: { icon: Smartphone, color: 'bg-teal-100 text-teal-600', label: 'SMS Received' },
  portal_message: { icon: MessageSquare, color: 'bg-purple-100 text-purple-600', label: 'Portal Message' },
  stage_change: { icon: GitBranch, color: 'bg-orange-100 text-orange-600', label: 'Stage Changed' },
  note_added: { icon: Edit3, color: 'bg-yellow-100 text-yellow-600', label: 'Note Added' },
  doc_uploaded: { icon: Upload, color: 'bg-indigo-100 text-indigo-600', label: 'Document Uploaded' },
  doc_downloaded: { icon: Download, color: 'bg-gray-100 text-gray-600', label: 'Document Downloaded' },
};

const PAGE_SIZE = 25;

export default function RecordActivityLog() {
  const { typeSlug, recordId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [summary, setSummary] = useState({});

  useEffect(() => { loadData(); }, [recordId, page, filter]);
  useEffect(() => { loadSummary(); }, [recordId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filter !== 'all') params.types = filter;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await client.get(`/communication-log/${recordId}`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await client.get(`/communication-log/${recordId}/summary`);
      setSummary(data);
    } catch (err) {}
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.set('record_id', recordId);
      if (filter !== 'all') params.set('types', filter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await client.get(`/communication-log/global/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_log_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleDownload = async (item) => {
    if (!item.file_id) return;
    try {
      const { data } = await client.get(`/documents/${item.file_id}/download`);
      window.open(data.url, '_blank');
    } catch (err) {}
  };

  const formatDate = (ts) => {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const filterOptions = [
    { value: 'all', label: 'All', count: summary.total },
    { value: 'doc_generated', label: 'Documents', count: summary.doc_generated },
    { value: 'email_sent', label: 'Emails', count: summary.email_sent },
    { value: 'sms_sent,sms_received', label: 'SMS', count: (summary.sms_sent || 0) + (summary.sms_received || 0) },
    { value: 'portal_message', label: 'Portal', count: summary.portal_message },
    { value: 'stage_change', label: 'Stage Changes', count: summary.stage_change },
    { value: 'note_added', label: 'Notes', count: summary.note_added },
    { value: 'internal_chat', label: 'Internal Chat', count: summary.internal_chat },
  ].filter(f => f.value === 'all' || f.count > 0);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Activity Log</h1>
            <p className="text-sm text-gray-500">{total} total entries</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadData()}
                  placeholder="Search subject, email..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setFilter('all'); setPage(0); }}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Clear All</button>
            <button onClick={() => { setPage(0); loadData(); }}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Apply</button>
          </div>
        </div>
      )}

      {/* Type Filters */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {filterOptions.map(f => (
          <button key={f.value}
            onClick={() => { setFilter(f.value); setPage(0); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition ${
              filter === f.value
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}>
            {f.label}
            {f.count > 0 && <span className="opacity-60">{f.count}</span>}
          </button>
        ))}
      </div>

      {/* Timeline List */}
      <div className="bg-white rounded-xl border border-gray-200">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400">No activity found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((item) => {
              const config = ACTIVITY_CONFIG[item.activity_type] || { icon: Clock, color: 'bg-gray-100 text-gray-500', label: item.activity_type };
              const Icon = config.icon;
              const dirIcon = item.direction === 'outbound' ? ArrowUpRight : item.direction === 'inbound' ? ArrowDownLeft : null;

              return (
                <div key={item.id} className="flex gap-4 px-5 py-4 hover:bg-gray-50 transition">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{config.label}</span>
                      {dirIcon && React.createElement(dirIcon, { className: 'w-3 h-3 text-gray-400' })}
                      {item.status && item.status !== 'completed' && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          item.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          item.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{item.status}</span>
                      )}
                      {item.template_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">
                          {item.template_name}
                        </span>
                      )}
                    </div>

                    {item.subject && <p className="text-sm text-gray-600 mt-0.5">{item.subject}</p>}

                    {(item.from_address || item.to_address) && (
                      <div className="flex gap-4 mt-1 text-xs text-gray-400">
                        {item.from_address && <span>From: {item.from_name ? item.from_name + ' ' : ''}&lt;{item.from_address}&gt;</span>}
                        {item.to_address && <span>To: {item.to_name ? item.to_name + ' ' : ''}&lt;{item.to_address}&gt;</span>}
                      </div>
                    )}

                    {item.file_name && (
                      <button onClick={() => handleDownload(item)}
                        className="flex items-center gap-1.5 mt-2 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition">
                        <FileText className="w-3 h-3" />
                        <span className="truncate max-w-[250px]">{item.file_name}</span>
                        {item.file_size && <span className="text-gray-400">({formatSize(item.file_size)})</span>}
                        <Download className="w-3 h-3 ml-1" />
                      </button>
                    )}

                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[11px] text-gray-400">
                        {item.performer_first_name
                          ? item.performer_first_name + ' ' + (item.performer_last_name || '')
                          : 'System'}
                      </span>
                      <span className="text-[11px] text-gray-300">•</span>
                      <span className="text-[11px] text-gray-400">{formatDate(item.created_at)}</span>
                      {item.ip_address && (
                        <>
                          <span className="text-[11px] text-gray-300">•</span>
                          <span className="text-[11px] text-gray-300">IP: {item.ip_address}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
